import type { ReactNode } from 'react';
import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';

export type CloseConfirmationOptions = {
  title: string;
  description: string;
  confirmText?: string;
  cancelText?: string;
  confirmVariant?: 'default' | 'destructive';
  showCancelButton?: boolean;
};

export type DialogOptions = {
  title?: string | ReactNode;
  description?: string | ReactNode;
  onConfirm?: () => void;
  onCancel?: () => void;
  confirmText?: string;
  cancelText?: string;
  showFooter?: boolean;
  className?: string;
  closeConfirmation?: CloseConfirmationOptions;
  closeConfirmationRequired?: boolean;
};

export type DialogComponentOptions = {
  component: ReactNode;
  onClose?: () => void;
  className?: string;
  showCloseButton?: boolean;
  customCloseButton?: ReactNode;
  preventOverlayClose?: boolean;
  closeConfirmation?: CloseConfirmationOptions;
  closeConfirmationRequired?: boolean;
  skipBackButtonConfirmation?: boolean;
};

export type DialogConfig = DialogOptions | DialogComponentOptions;

interface ModalStoreState {
  isOpen: boolean;
  dialogConfig: DialogConfig | null;
  isCloseConfirmationRequired: boolean;
  isSkipBackButtonConfirmation: boolean;
  setIsOpen: (isOpen: boolean) => void;
  setDialogConfig: (dialogConfig: DialogConfig | null) => void;
  setCloseConfirmationRequired: (required: boolean) => void;
  setSkipBackButtonConfirmation: (skip: boolean) => void;

  showDialog: (options: DialogOptions) => void;
  showDialogComponent: (options: DialogComponentOptions) => void;
  closeDialog: () => void;

  // State management
  state: Record<string, unknown>;
  setState: <K extends string>(key: K, value: unknown) => void;
  getState: <K extends string>(key: K) => unknown;
  getAllState: <T = Record<string, unknown>>() => T;
  setMultipleState: <T>(updates: Record<string, unknown> | T) => void;

  // Cleanup
  cleanup: () => void;
}

export const useModalStore = create<ModalStoreState>()(
  subscribeWithSelector((set, get) => ({
    isOpen: false,
    dialogConfig: null,
    isCloseConfirmationRequired: false,
    isSkipBackButtonConfirmation: false,
    setIsOpen: isOpen => set({ isOpen }),
    setDialogConfig: dialogConfig => set({ dialogConfig }),
    setCloseConfirmationRequired: required =>
      set({ isCloseConfirmationRequired: required }),
    setSkipBackButtonConfirmation: skip =>
      set({ isSkipBackButtonConfirmation: skip }),
    showDialog: options =>
      set({
        isOpen: true,
        dialogConfig: options as DialogOptions,
        isCloseConfirmationRequired: options.closeConfirmationRequired ?? false,
      }),
    showDialogComponent: options =>
      set({
        isOpen: true,
        dialogConfig: options as DialogComponentOptions,
        isCloseConfirmationRequired: options.closeConfirmationRequired ?? false,
        isSkipBackButtonConfirmation:
          options.skipBackButtonConfirmation ?? false,
      }),
    closeDialog: () => set({ isOpen: false }),

    // State management
    state: {},
    setState: (key, value) =>
      set(state => ({
        state: { ...state.state, [key]: value },
      })),
    getState: key => get().state[key],
    getAllState: <T = Record<string, unknown>>() => ({ ...get().state }) as T,
    setMultipleState: updates =>
      set(state => ({
        state: { ...state.state, ...updates },
      })),

    // Cleanup
    cleanup: () => {
      set({
        state: {},
        isCloseConfirmationRequired: false,
        isSkipBackButtonConfirmation: false,
      });
    },
  }))
);

// Hook for easier usage with TypeScript generics
export function useModalStoreState<T = Record<string, unknown>>() {
  const store = useModalStore();

  return {
    state: store.state as T,
    setState: <K extends keyof T>(key: K, value: T[K]) =>
      store.setState(key as string, value),
    getState: <K extends keyof T>(key: K): T[K] =>
      store.getState(key as string) as T[K],
    getAllState: () => store.getAllState() as T,
    setMultipleState: (updates: Partial<T>) =>
      store.setMultipleState(updates as Record<string, unknown>),
    cleanup: store.cleanup,
    setCloseConfirmationRequired: store.setCloseConfirmationRequired,
    setSkipBackButtonConfirmation: store.setSkipBackButtonConfirmation,
  };
}
